import { UserRole } from '@today/types'
import { roleAtLeast } from '@/auth/role-order'
import { ADMIN_SECTION_CATALOG, type AdminSectionEntry } from './catalog'
import { ADMIN_PLUGIN_SECTIONS, type AdminPluginSection } from './plugin-registry'

/** Who is looking: coarse role, permission atoms (`/auth/me`) and own tenant slug (`null` = unresolved). */
export interface AdminSectionViewer {
	role: UserRole
	permissions: readonly string[]
	slug: string | null
}

/** One entry of the admin shell's menu. */
export interface AdminSectionLink {
	key: string
	label: string
	href: string
}

const holdsGrant = (section: AdminSectionEntry, viewer: AdminSectionViewer) =>
	'permission' in section.grant
		? viewer.permissions.includes(section.grant.permission)
		: roleAtLeast(viewer.role, section.grant.role)

/** The `/admin/plugins/{code}` route of a plugin section. */
export const adminPluginHref = (code: string) => `/admin/plugins/${code}`

/** Whether `viewer` may open plugin section `section` (own tenant + permission). */
export const canOpenPluginSection = (section: AdminPluginSection, viewer: AdminSectionViewer) =>
	viewer.slug !== null && section.tenantSlug === viewer.slug && viewer.permissions.includes(section.permission)

/**
 * The admin shell's menu for `viewer` (TO-189 REQ-002/003/005): core sections the
 * viewer holds the grant for, in catalog order — tenant-scoped ones only once the
 * viewer's own slug is resolved — then their tenant's plugin sections.
 */
export function visibleAdminSections(
	viewer: AdminSectionViewer,
	plugins: readonly AdminPluginSection[] = ADMIN_PLUGIN_SECTIONS,
): AdminSectionLink[] {
	const { slug } = viewer
	const core = ADMIN_SECTION_CATALOG.filter(
		(section) => holdsGrant(section, viewer) && (!section.tenantScoped || slug !== null),
	).map((section) => ({
		key: section.key,
		label: section.label,
		href: section.tenantScoped ? `/t/${slug}/admin${section.path}` : section.path,
	}))
	const fromPlugins = plugins
		.filter((section) => canOpenPluginSection(section, viewer))
		.map((section) => ({ key: `plugin:${section.code}`, label: section.label, href: adminPluginHref(section.code) }))
	return [...core, ...fromPlugins]
}

/** Every permission some core or plugin admin section requires (the sidebar's Admin entry, REQ-008). */
export function adminRequiredPermissions(
	plugins: readonly AdminPluginSection[] = ADMIN_PLUGIN_SECTIONS,
): ReadonlySet<string> {
	const required = new Set<string>()
	for (const { grant } of ADMIN_SECTION_CATALOG) if ('permission' in grant) required.add(grant.permission)
	for (const { permission } of plugins) required.add(permission)
	return required
}

/**
 * Whether the app sidebar shows the Admin entry (TO-189 REQ-008): the viewer is a
 * Super-Admin or holds some permission an admin section requires. Slug-agnostic —
 * the shell itself still settles tenant-scoped sections.
 */
export function canOpenAdmin(
	viewer: { role: UserRole; permissions?: readonly string[] },
	plugins: readonly AdminPluginSection[] = ADMIN_PLUGIN_SECTIONS,
): boolean {
	if (roleAtLeast(viewer.role, UserRole.SUPER_ADMIN)) return true
	const required = adminRequiredPermissions(plugins)
	return viewer.permissions?.some((permission) => required.has(permission)) ?? false
}
